import { useAdFleetMapVehicles } from '@backoffice-frontend/ad-operations'
import { useAdOperatorAvailability } from '@backoffice-frontend/ad-operator-availability'
import { useFlatTireSimulatedVehicleIds } from '@backoffice-frontend/fleet-maps-common'
import { FccAdAlertType } from '@backoffice-frontend/graphql'
import { LoadingIndicator, Text } from '@moia-dev/pace-web'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AdAlertFeedCard } from '../AdAlertFeedCard/AdAlertFeedCard'
import { getActiveAlerts } from '../AdAlertFeedCard/utils'
import { useAlertSounds } from '../AlertSoundsToggle/useAlertSounds'
import { DRAWER_WIDTH } from '../constants'
import { FlatTireAlertFeedCard } from '../FlatTireAlertFeedCard/FlatTireAlertFeedCard'
import { useIsHandlingAlert } from '../useIsHandlingAlert'

const OPERATOR_INITIATED_ALERTS: FccAdAlertType[] = [
  FccAdAlertType.OperatorToCabinCall,
  FccAdAlertType.OutgoingOperatorToSafetyDriverCall,
]

export const AdAlertFeed = () => {
  const { t } = useTranslation()
  const { vehicles, loadingVehicles } = useAdFleetMapVehicles()
  const simulatedVehicleIds = useFlatTireSimulatedVehicleIds()
  const { isBusy } = useAdOperatorAvailability()
  const isHandlingAlert = useIsHandlingAlert()
  const { playAlertSound } = useAlertSounds()

  const prevVehicleIdsRef = useRef<Set<string>>(new Set())
  const hasInitializedRef = useRef(false)

  const vehiclesWithAlerts = useMemo(() => {
    return vehicles
      .filter(vehicle => getActiveAlerts(vehicle).length > 0)
      .sort((a, b) => getOldestAlertDate(a) - getOldestAlertDate(b))
  }, [vehicles])

  const isCurrentUserBusy = isBusy || isHandlingAlert

  useEffect(() => {
    if (loadingVehicles) return

    const currentIds = new Set(vehiclesWithAlerts.map(v => v.id))
    const prevIds = prevVehicleIdsRef.current

    const newVehicles = vehiclesWithAlerts.filter(v => !prevIds.has(v.id))

    const hasNewNonUserInitiatedAlerts = newVehicles.some(vehicle =>
      getActiveAlerts(vehicle).some(
        alert => !OPERATOR_INITIATED_ALERTS.includes(alert.type),
      ),
    )

    if (hasNewNonUserInitiatedAlerts && hasInitializedRef.current && !isBusy) {
      playAlertSound()
    }

    hasInitializedRef.current = true
    prevVehicleIdsRef.current = currentIds
  }, [vehiclesWithAlerts, playAlertSound, loadingVehicles, isBusy])

  if (loadingVehicles) {
    return (
      <div
        css={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexGrow: 1,
        }}
      >
        <LoadingIndicator />
      </div>
    )
  }

  return (
    <div
      css={theme => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.space.Space2,
        padding: theme.space.Space2,
        overflowY: 'auto',
        overflowX: 'hidden',
        flexGrow: 1,
      })}
    >
      <AnimatePresence mode="popLayout">
        {[...simulatedVehicleIds].map(vehicleId => (
          <motion.div
            key={`flat-tire-${vehicleId}`}
            initial={{ x: -DRAWER_WIDTH, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -DRAWER_WIDTH, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            layout
          >
            <FlatTireAlertFeedCard vehicleId={vehicleId} />
          </motion.div>
        ))}
        {vehiclesWithAlerts.map(vehicle => (
          <motion.div
            key={vehicle.id}
            initial={{ x: -DRAWER_WIDTH, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -DRAWER_WIDTH, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            layout
          >
            <AdAlertFeedCard
              vehicle={vehicle}
              isCurrentUserBusy={isCurrentUserBusy}
            />
          </motion.div>
        ))}
      </AnimatePresence>
      {vehiclesWithAlerts.length === 0 && simulatedVehicleIds.size === 0 && (
        <div
          css={theme => ({
            position: 'absolute',
            top: '40%',
            left: 0,
            right: 0,
            transform: 'translateY(-50%)',
            display: 'flex',
            justifyContent: 'center',
            padding: theme.space.Space2,
            pointerEvents: 'none',
          })}
        >
          <Text
            size="sm"
            css={theme => ({ color: theme.semantic.ColorContentReduced })}
          >
            {t('adOperatorDrawer_alertFeed_noAlerts_label')}
          </Text>
        </div>
      )}
    </div>
  )
}

const getOldestAlertDate = (
  vehicle: Parameters<typeof getActiveAlerts>[0],
): number => {
  const activeAlerts = getActiveAlerts(vehicle)

  if (activeAlerts.length === 0) return Date.now()

  const oldestDate = activeAlerts.reduce((oldest, alert) => {
    const alertDate = new Date(alert.createdAt).getTime()
    return alertDate < oldest ? alertDate : oldest
  }, Date.now())

  return oldestDate
}
